import { useScreenSize } from '@/themes/media-query'
import { DataGrid, Popup } from 'devextreme-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoaderData } from 'react-router-typesafe'
import {
  Button as GridButton,
  Column,
  ColumnChooser,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Item,
  MasterDetail,
  Pager,
  Scrolling,
  Search,
  SearchPanel,
  Selection,
  Sorting,
  StateStoring,
  Toolbar,
} from 'devextreme-react/data-grid'
import { CellPreparedEvent, ColumnButtonClickEvent, EditorPreparingEvent } from 'devextreme/ui/data_grid'
import notify from 'devextreme/ui/notify'
import { assistenza, ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { GridCellColor } from '@/enums'
import { Workbook } from 'exceljs'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { ClickEvent } from 'devextreme/ui/button'
import { ticketsPageRouteLoader } from '@/routes/tickets/TicketsPage.route'
import { StatoTicket } from '@/routes/tickets/TicketEditor.enums'
import { useAzureManager } from '@/auth/azure/azureManager'
import TicketBriefDetail from '@/routes/tickets/TicketBriefDetail'
import { isUserRoleAllowed, onDataGridSelectedRowButtonClick } from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { AssignSelector } from '@/routes/tickets/AssignSelector'
import { PopupRef } from 'devextreme-react/cjs/popup'
import { gridTotalItemsLabel } from '@/constants'
import Rating from '@/components/Rating/Rating'
import { PhoneNumber } from '@/components/PhoneNumber/PhoneNumber'
import DocumentHead from '@/components/document-head/DocumentHead'
import { createRoot } from 'react-dom/client'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ObjectCodeLink } from '@/components/object-code-link/ObjectCodeLink'
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import './TicketsPage.scss'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrayStore, DataSource } from 'devextreme/common/data'
import { confirm } from 'devextreme/ui/dialog'
import {
  useTicketTakeChargeMutation,
  useTicketReleaseMutation,
  useTicketRemoveAssigneeMutation,
  useTicketAssignToMutation,
  useTicketCancelMutation,
  useTicketSendClosureNoticeMutation,
  useTicketSendContactNoticeMutation,
  useTicketSetUrgentMutation,
  useTicketClearWaitMutation,
  useTicketSetWaitMutation,
  useTicketSendSupportExpiredNoticeMutation,
  useTicketSendOtherVendorNoticeMutation,
  AssignToMutationParams,
  useTicketIncrementCallNumberMutation,
  useTicketSetActivityRequestMutation,
  useTicketSendSupportSuspendedNoticeMutation,
} from '@/routes/tickets/utils/mutations'
import { GlobalLoadingComponent } from '@/components/GlobalLoadingComponent/GlobalLoadingComponent'
import { AxiosError, AxiosResponse } from 'axios'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import { Providers } from '@microsoft/mgt-element'
import { duplicateTicketWithApi } from '@/routes/tickets/ticketWrite'
import { buildTicketNoteContext, getTicketNotesQueryOptions } from '@/routes/tickets/notes/ticketNotes'

const TicketsPage = () => {
  const { userInfo } = useLoaderData<typeof ticketsPageRouteLoader>()
  const defaultCRUDAllowedRoles = useMemo(() => {
    return [Roles.GlobalAdministrator, Roles.Administrator, Roles.Supervisor, Roles.TechDeveloper]
  }, [])
  const navigate = useNavigate()
  const gridRef = useRef<DataGridRef>(null)
  const [selectedTicket, setSelectedTicket] = useState<ticket | null>(null)
  const { clientId } = useParams()
  const { client } = useQsAdminApiManager()
  const [loadPanelVisible, setLoadPanelVisible] = useState<boolean>(false)
  const queryClient = useQueryClient()
  const assignToMutation = useTicketAssignToMutation(clientId)
  const { data: azienda } = useQuery({
    queryKey: ['azienda', clientId],
    queryFn: async () => {
      if (!clientId) return undefined
      const result = await useQsAdminApiManager
        .getState()
        .service.azienda(Number(clientId))
        .query((builderQuery, aziendaEntity) => {
          builderQuery.select('id', 'nome')
        })
      return result.data
    },
    enabled: !!clientId,
  })
  const title = azienda ? `${azienda.nome} - Ticket` : 'Ticket'
  const currentScreenSize = useScreenSize()
  const takeChargeMutation = useTicketTakeChargeMutation(clientId)
  const releaseMutation = useTicketReleaseMutation(clientId)
  const removeAssigneeMutation = useTicketRemoveAssigneeMutation(clientId)
  const setWaitMutation = useTicketSetWaitMutation(clientId)
  const cancelMutation = useTicketCancelMutation(clientId)
  const setUrgentMutation = useTicketSetUrgentMutation(clientId)
  const incrementCallNumberMutation = useTicketIncrementCallNumberMutation(clientId)
  const sendClosureNoticeMutation = useTicketSendClosureNoticeMutation(clientId)
  const setClearWaitMutation = useTicketClearWaitMutation(clientId)
  const sendContactNoticeMutation = useTicketSendContactNoticeMutation(clientId)
  const sendSupportExpiredNoticeMutation = useTicketSendSupportExpiredNoticeMutation(clientId)
  const sendSupportSuspendedNoticeMutation = useTicketSendSupportSuspendedNoticeMutation(clientId)
  const sendOtherVendorNoticeMutation = useTicketSendOtherVendorNoticeMutation(clientId)
  const setActivityRequestMutation = useTicketSetActivityRequestMutation(clientId)

  const getGridHeight = useCallback(() => {
    let h = '84vh'
    if (currentScreenSize.isMedium) {
      h = '80vh'
    } else if (currentScreenSize.isSmall) {
      h = '125vh'
    } else if (currentScreenSize.isXSmall) {
      h = '80vh'
    }
    return h
  }, [currentScreenSize])

  const popupAssignRef = useRef<PopupRef>(null)

  const {
    data: ticketsData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['tickets', 'active', clientId],
    queryFn: async () => {
      const result = await useQsAdminApiManager
        .getState()
        .service.ticket()
        .query((builderQuery, ticketEntity) => {
          builderQuery
            .filter(!clientId ? ticketEntity.stato.props.id.in(0, 1) : undefined)
            .filter(clientId ? ticketEntity.sede.props.azienda.props.id.eq(Number(clientId)) : undefined)
            .expanding('sede', (builderSede, sede) => {
              builderSede
                .expanding('assistenze', (builderAss, assistenzaEntity) => {
                  builderAss.expand('telefonica').expand('maintenance')
                })
                .expanding('azienda', (builderAzienda, azienda) => {
                  builderAzienda
                    .expanding('agente', (builderAgente, agente) => {
                      builderAgente.expand('commerciale_qs')
                    })
                    .expand('rivenditore')
                })
            })
            .expand('tipologia_ticket')
            .expand('assegnato')
            .expand('tipologia_problema')
            .expand('argomento_problema')
            .expand('piattaforma')
            .expand('applicazione')
            .expand('creatore')
            .expand('incaricato')
            .expand('stato')
            .expanding('eventi', (builderEventi, eventi) => {
              builderEventi.expand('utente').expand('tipologia')
            })
            .expanding('dumps', (builderDumps, dumps) => {
              builderDumps.expand('stato')
              builderDumps.expand('licenza')
            })
        })
      return result.data.value
    },
  })

  const loadingPanelConfig: DataGridTypes.LoadPanel = useMemo(
    () => ({
      enabled: isLoading,
      showIndicator: true,
      showPane: true,
      shading: true,
      text: 'Loading…',
    }),
    [isLoading],
  )

  const groupPanelConfig: DataGridTypes.GroupPanel = useMemo(
    () => ({
      visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
      emptyPanelText: 'Trascina qui una colonna per raggruppare',
    }),
    [currentScreenSize],
  )

  const refreshButtonOptions = useMemo(
    () => ({
      hint: 'Refresh',
      text: 'Refresh',
      icon: 'refresh',
      onClick: async () => {
        const done = await refetch()
        if (done) {
          notify({
            message: 'Dati aggiornati',
            type: 'success',
            displayTime: 2000,
          })
        } else {
          notify({
            message: "Errore durante l'aggiornamento dei dati",
            type: 'error',
            displayTime: 2000,
          })
        }
      },
      stylingMode: 'text',
    }),
    [refetch],
  )
  const newTicketButtonOptions = useMemo(
    () => ({
      hint: 'Nuovo Ticket',
      text: 'Nuovo Ticket',
      icon: 'plus',
      onClick: () => {
        navigate('new')
      },
      stylingMode: 'text',
    }),
    [navigate],
  )
  const historicButtonOptions = useMemo(
    () => ({
      hint: 'Storico ticket',
      text: 'Storico ticket',
      icon: 'eyeopen',
      onClick: () => {
        navigate('historic')
      },
      stylingMode: 'text',
    }),
    [navigate],
  )

  const handleDuplicateTicketClick = useCallback(
    (e: ClickEvent) => {
      onDataGridSelectedRowButtonClick(gridRef, (rowdata: ticket) => {
        console.log('ticket', rowdata)
        const result = confirm(
          `<i>Vuoi davvero duplicare il ticket <b>${rowdata.ded_Dis}</b>?</i>`,
          'Duplicazione ticket',
        )
        result.then((dialogResult) => {
          if (dialogResult === false) return
          setLoadPanelVisible(true)
          duplicateTicketWithApi(client, rowdata.id)
            .then((response: AxiosResponse) => {
              console.log('response', response.data)
              window.open(`/tickets/${response.data.ticketRecordId}`, '_blank')
            })
            .catch((error: AxiosError) => {
              notify(`Errore duplicazione ticket. Dettagli : ${error}`, 'error', 2000)
            })
            .finally(() => {
              setLoadPanelVisible(false)
            })
        })
      })
    },
    [client],
  )
  const duplicateButtonOptions = useMemo(
    () => ({
      hint: 'Duplica ticket',
      text: 'Duplica ticket',
      icon: 'copy',
      onClick: handleDuplicateTicketClick,
      stylingMode: 'text',
    }),
    [handleDuplicateTicketClick],
  )
  const supportManulButtonOptions = useMemo(
    () => ({
      hint: 'Manuale assistenza',
      text: 'Manuale assistenza',
      icon: 'docxfile',
      onClick: async (e: ClickEvent) => {
        const supportManualPath = '_Templates/WEBADMIN-Gestione Ticket.docx'
        try {
          gridRef?.current?.instance().beginCustomLoading('Apertura manuale in corso....')
          const response = await Providers.globalProvider.graph
            .api(`drives/${import.meta.env.VITE_QSADMINSITE_DRIVE_ID}/root:/${supportManualPath}`)
            .get()
          window.open(response.webUrl, '_blank')
        } catch (error) {
          notify(`Errore individuazione manuale assistenza. Dettagli : ${error}`, 'error', 4000)
        } finally {
          gridRef?.current?.instance().endCustomLoading()
        }
      },
      stylingMode: 'text',
    }),
    [],
  )

  const { data: impiegatiData } = useQuery({
    queryKey: ['impiegati'],
    queryFn: async () => {
      const result = await useQsAdminApiManager.getState().service.user().query()
      return result.data.value
    },
  })
  const { data: tipologieTicketData } = useQuery({
    queryKey: ['tipologie_ticket'],
    queryFn: async () => {
      const result = await useQsAdminApiManager.getState().service.tipologia_ticket().query()
      return result.data.value
    },
  })
  type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>
  }
  const updateQueryData = useCallback(
    (currentTicket: ticket, newData: RecursivePartial<ticket>) => {
      queryClient.setQueryData(['tickets', 'active', clientId], (oldData: ticket[] | undefined) => {
        if (!oldData) return oldData
        return oldData.map((ticketItem) => {
          if (ticketItem.id === currentTicket.id) {
            return {
              ...ticketItem,
              ...newData,
            }
          }
          return ticketItem
        })
      })
    },
    [queryClient, clientId],
  )

  const [connection, setConnection] = useState<HubConnection | null>(null)

  useEffect(() => {
    let currentConnection: HubConnection | null = null

    const setupSignalRConnection = async () => {
      try {
        currentConnection = new HubConnectionBuilder()
          .withUrl(`${import.meta.env.VITE_QSADMINAPI_HOST}/signalr/changes`, {
            accessTokenFactory: async () => await useAzureManager.getState().getToken(),
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect()
          .configureLogging(import.meta.env.PROD ? LogLevel.Error : LogLevel.Warning)
          .build()
        currentConnection.on('EntityChangedV1', async (_, entityName: string) => {
          console.log(`Entity changed: ${entityName}`)
          if (entityName === 'ticket') {
            // Use TanStack Query's invalidateQueries for built-in deduplication
            console.log('Ticket entity changed via SignalR V1 - invalidating queries')
            await queryClient.invalidateQueries({
              queryKey: ['tickets', 'active', clientId],
              exact: true,
            })
          }
        })
        // currentConnection.on('EntityChangedV2', async (payload: EntityChangedPayload, entityName: string) => {
        //   console.log(`Entity changed V2: ${entityName}`, payload)
        //   if (entityName === 'ticket') {
        //     // Use TanStack Query's invalidateQueries for built-in deduplication
        //     console.log('Ticket entity changed via SignalR V2 - invalidating queries')
        //     await queryClient.invalidateQueries({
        //       queryKey: ['tickets', 'active', clientId],
        //       exact: true,
        //     })
        //   }
        // })
        currentConnection.onreconnected(async () => {
          console.log('Tickets SignalR connection reestablished')
          await currentConnection?.invoke('WatchEntityV1', 'ticket')
          // Use TanStack Query's invalidateQueries for built-in deduplication
          console.log('SignalR reconnected - invalidating queries')
          await queryClient.invalidateQueries({
            queryKey: ['tickets', 'active', clientId],
            exact: true,
          })
        })

        // await currentConnection.start()
        console.log('SignalR connection established for ticket changes')

        await currentConnection.invoke('WatchEntityV1', 'ticket')
        console.log('Watching ticket entity changes')

        setConnection(currentConnection)
      } catch (connectionError) {
        console.error('Error setting up SignalR connection:', connectionError)
      }
    }

    // setupSignalRConnection()

    return () => {
      if (currentConnection) {
        console.log('Cleaning up SignalR connection...')
        currentConnection.stop()
      }
    }
  }, [clientId, queryClient])

  const cellPrepared = useCallback(
    (e: CellPreparedEvent) => {
      if (e.rowType === 'data' && e.value) {
        if (e.column.dataField === 'assegnato.fullname' && e.value === userInfo?.name)
          e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.LILAC}`

        if (e.column.dataField === 'incaricato.fullname')
          e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.ORANGE}`

        if (e.column.name === 'info_maintenance') {
          switch (e.value) {
            case 'ATTIVA': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`

              break
            }
            case 'SCADUTA': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`

              break
            }
            case 'SOSPESA': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`

              break
            }
            default: {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
            }
          }
        }

        if (e.column.name === 'info_telefonica') {
          switch (e.value) {
            case 'STANDARD': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.EMERALD}`

              break
            }
            case 'SCADUTA': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`

              break
            }
            case 'SOSPESA': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.ORANGE}`

              break
            }
            case 'PRIME': {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SKYBLUE}`

              break
            }
            default: {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
            }
          }
        }

        if (e.column.dataField === 'ded_Dis') {
          switch (e.row.data.leg_n_chiamate) {
            case 0: {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
              break
            }
            case 1: {
              e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.ORANGE}`
              break
            }
            case 2: {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`
              break
            }
            default: {
              e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: white; background-color: ${GridCellColor.RED}`
              break
            }
          }
        }
      }
      if (e.rowType === 'data' && !e.value && e.column.dataField === 'assegnato.fullname')
        e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.LIGHTGRREN}`

      if (e.column.dataField === 'tipologia_ticket.nome' && e.value === `RICHIESTA DI ATTIVITA'`)
        e.cellElement.style.cssText = `color: black; background-color: ${GridCellColor.DEEPSKYBLUE}`
    },
    [userInfo?.name],
  )

  const exportingHandler = useCallback(async (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('Tickets')
    exportDataGrid({ component: e.component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Tickets.xlsx')
      })
    })
  }, [])
  const rowDblClickHandler = useCallback(
    (e: DataGridTypes.RowDblClickEvent) => {
      if (e.rowType === 'data') navigate(`${e.data.id}`)
    },
    [navigate],
  )
  const prefetchTicketNotes = useCallback(
    async (rowTicket: ticket | null | undefined) => {
      const ticketNoteContext = buildTicketNoteContext(rowTicket)
      if (!ticketNoteContext) return

      await queryClient.prefetchQuery(getTicketNotesQueryOptions(client, ticketNoteContext))
    },
    [client, queryClient],
  )
  const rowExpandingHandler = useCallback(
    (e: DataGridTypes.RowExpandingEvent<ticket, number>) => {
      if (e.key == null) return

      const rowTicket = ticketsData?.find((ticketItem) => ticketItem.id === Number(e.key))
      prefetchTicketNotes(rowTicket)
    },
    [prefetchTicketNotes, ticketsData],
  )
  const rowClickHandler = useCallback(
    (e: DataGridTypes.RowClickEvent<ticket, number>) => {
      if (e.rowType !== 'data') return
      prefetchTicketNotes(e.data)
    },
    [prefetchTicketNotes],
  )
  const editorPreparingHandler = useCallback((e: EditorPreparingEvent) => {
    if (e.parentType === 'filterRow' && e.dataField === 'rating') {
      e.editorName = 'dxSelectBox'
      e.editorOptions.showClearButton = true
      e.editorOptions.dataSource = [1, 2, 3, 4, 5]
      e.editorOptions.itemTemplate = (data: any) => {
        const container = document.createElement('div')
        const root = createRoot(container)
        root.render(<Rating value={data} />)
        return container
      }
    }
  }, [])

  const contextMenuPreparingHandler = useCallback(
    (e: DataGridTypes.ContextMenuPreparingEvent) => {
      console.log('e', e)
      if (e.row?.rowType === 'data') {
        const rowTicket: ticket = e.row.data
        if (!e.items) e.items = []
        e.items.push(
          {
            text: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles) ? 'Edita' : 'Visualizza',
            icon: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles) ? 'edit' : 'eyeopen',
            visible: true,
            onItemClick: () => {
              navigate(`${e.row?.data.id}`)
            },
          },
          {
            text: 'Prendi in carico',
            icon: 'cart',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              !rowTicket?.incaricato &&
              (!rowTicket.assegnato || rowTicket.assegnato.email === userInfo?.email),
            onItemClick: async () => {
              const currentUser = impiegatiData?.find((u) => u.email === userInfo?.email)
              const needsConfirmation =
                rowTicket?.assegnato && rowTicket.assegnato?.email !== userInfo?.email
                  ? await confirm(
                      rowTicket.incaricato && rowTicket.incaricato.email !== userInfo?.email
                        ? `<i>Il ticket<b> ${rowTicket.ded_Dis}</b> è in carico all'utente ${rowTicket.incaricato.fullname}. Si desidera procedere ugualmente?</i>`
                        : `<i>Il ticket<b> ${rowTicket.ded_Dis}</b> è assegnato all'utente ${rowTicket.assegnato?.fullname}. Si desidera procedere ugualmente?</i>`,
                      'Presa in Carico Ticket',
                    )
                  : true
              if (currentUser && needsConfirmation) {
                updateQueryData(rowTicket, {
                  incaricato: currentUser,
                  assegnato: rowTicket.assegnato || currentUser,
                })
                takeChargeMutation.mutate({ ticket: rowTicket })
              }
            },
          },
          {
            text: 'Assegna a me',
            icon: 'user',
            visible:
              isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.TechDeveloper,
                Roles.Supervisor,
                Roles.Administrator,
              ]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              !rowTicket.assegnato,
            onItemClick: async () => {
              const currentUser = impiegatiData?.find((u) => u.email === userInfo?.email)
              if (currentUser && rowTicket) {
                assignToMutation.mutate({
                  ticketRecordId: rowTicket.id,
                  assigneeUser: currentUser,
                } satisfies AssignToMutationParams)
              }
            },
          },
          {
            text: 'Assegna a',
            icon: 'user',
            visible:
              isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Supervisor,
                Roles.TechDeveloper,
                Roles.Administrator,
              ]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              !rowTicket.assegnato,
            onItemClick: () => {
              setSelectedTicket(rowTicket)
              console.log('currently selected ticket', rowTicket)
              popupAssignRef.current?.instance().show()
            },
          },
          {
            text: 'Rimuovi assegnazione',
            icon: 'arrowback',
            visible:
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket?.assegnato &&
              (isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.Supervisor, Roles.Administrator]) ||
                (isUserRoleAllowed(userInfo?.roles, [Roles.TechDeveloper]) &&
                  rowTicket.assegnato.email === userInfo?.email)),
            onItemClick: async () => {
              const needsConfirmation =
                rowTicket?.assegnato && rowTicket?.assegnato.email !== userInfo?.email
                  ? await confirm(
                      `<i>Il ticket <b>${rowTicket.ded_Dis}</b> è assegnato all'utente ${rowTicket.assegnato?.fullname}. Si desidera procedere ugualmente?</i>`,
                      'Rimozione Assegnazione Ticket',
                    )
                  : true
              if (needsConfirmation) {
                try {
                  await removeAssigneeMutation.mutateAsync({ ticket: rowTicket })
                  updateQueryData(rowTicket, {
                    assegnato: null,
                    incaricato: null,
                  })
                } catch {
                  // Error already handled by mutation's onError
                }
              }
            },
          },
          {
            text: 'Rilascia',
            icon: 'arrowback',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket.incaricato &&
              rowTicket.incaricato.email === userInfo?.email,
            onItemClick: async () => {
              const needsConfirmation =
                rowTicket?.incaricato && rowTicket?.incaricato.email !== userInfo?.email
                  ? await confirm(
                      `<i>Il ticket <b>${rowTicket.ded_Dis}</b> è in carico all'utente ${rowTicket.incaricato.fullname}. Si desidera procedere ugualmente?</i>`,
                      'Rilascio Ticket',
                    )
                  : true
              if (needsConfirmation) {
                updateQueryData(rowTicket, {
                  incaricato: null,
                })
                releaseMutation.mutate({ ticket: rowTicket })
              }
            },
          },
          {
            text: 'Elimina',
            icon: 'close',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket?.assegnato &&
              rowTicket?.assegnato.email === userInfo?.email,
            onItemClick: async () => {
              if (
                await confirm(
                  `<i>Vuoi davvero eliminare il ticket <b>${rowTicket?.ded_Dis}</b> di <b>${rowTicket.leg_contatto}</b> dell' azienda <b>${rowTicket.sede?.azienda?.nome ?? 'non definita'}</b>?</i>`,
                  'Eliminazione Ticket',
                )
              ) {
                queryClient.setQueryData(['tickets', 'active', clientId], (oldData: ticket[] | undefined) => {
                  if (!oldData) return oldData
                  return oldData.filter((ticketItem) => ticketItem.id !== rowTicket.id)
                })
                cancelMutation.mutate({ ticket: rowTicket })
              }
            },
          },
          {
            text: 'Invia notifica chiusura',
            icon: 'bell',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket?.assegnato &&
              rowTicket?.assegnato.email === userInfo?.email,
            onItemClick: async () => {
              const conditions = [
                !rowTicket?.leg_email_contatto
                  ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : true,
                !rowTicket?.sede
                  ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero inviare la notifica di chiusura del ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      'Invio Notifica Chiusura Ticket',
                    ),
              ]
              if (conditions.every((condition) => condition === true)) {
                updateQueryData(rowTicket, { notifica_chiusura: true })
                sendClosureNoticeMutation.mutate({ ticket: rowTicket })
              }
            },
          },
          {
            text: 'Invia notifica contatto',
            icon: 'bell',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket?.assegnato &&
              rowTicket?.assegnato.email === userInfo?.email,
            onItemClick: async () => {
              const conditions = [
                !rowTicket?.leg_email_contatto
                  ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : true,
                !rowTicket?.sede
                  ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero inviare la notifica di contatto per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      `Invio Notifica Contatto Ticket`,
                    ),
              ]
              if (conditions.every((condition) => condition === true)) {
                updateQueryData(rowTicket, { contattato: true })
                sendContactNoticeMutation.mutate({ ticket: rowTicket })
              }
            },
          },
          {
            text: 'Incrementa chiamate',
            icon: 'arrowup',
            visible: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles),
            onItemClick: async () => {
              const needsConfirmation = await confirm(
                `<i>Vuoi davvero incrementare il numero di chiamate per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                `Incrementa Chiamate Ticket`,
              )
              if (needsConfirmation) {
                updateQueryData(rowTicket, {
                  leg_n_chiamate: rowTicket.leg_n_chiamate + 1,
                })
                incrementCallNumberMutation.mutate({
                  ticket: rowTicket,
                })
              }
            },
          },
          {
            text: 'Imposta richiesta di attività',
            icon: 'fas fa-hand',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket.tipologia_ticket?.nome !== `RICHIESTA DI ATTIVITA'` &&
              ((rowTicket?.assegnato && rowTicket?.assegnato.email === userInfo?.email) || !rowTicket?.assegnato),
            onItemClick: async () => {
              const conditions = [
                await confirm(
                  `<i>Vuoi davvero impostare il ticket <b>${rowTicket.ded_Dis}</b> come <b>RICHIESTA DI ATTIVITA'</b>?</i>`,
                  `Imposta Richiesta di Attività`,
                ),
              ]
              if (conditions.every((condition) => condition === true)) {
                const type = tipologieTicketData?.find((tt) => tt.nome === "RICHIESTA DI ATTIVITA'")
                updateQueryData(rowTicket, {
                  tipologia_ticket: type,
                })
                setActivityRequestMutation.mutate({
                  ticket: rowTicket,
                })
              }
            },
          },
          {
            text: 'Imposta urgente',
            icon: 'errorcircle',
            visible: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles),
            onItemClick: async () => {
              const conditions = [
                rowTicket.urgente
                  ? notify('Il ticket è già impostato come urgente.', 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero impostare l' urgenza per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      `Imposta Urgenza Ticket`,
                    ),
              ]
              if (conditions.every((condition) => condition === true)) {
                updateQueryData(rowTicket, {
                  urgente: true,
                })
                setUrgentMutation.mutate({
                  ticket: rowTicket,
                })
              }
            },
          },
          {
            text: 'Imposta attesa',
            icon: 'clock',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              !rowTicket.attesa &&
              rowTicket?.assegnato &&
              rowTicket?.assegnato.email === userInfo?.email,
            onItemClick: async () => {
              updateQueryData(rowTicket, {
                attesa: true,
              })
              setWaitMutation.mutate({
                ticket: rowTicket,
              })
            },
          },
          {
            text: 'Pulisci attesa',
            icon: 'clock',
            visible:
              isUserRoleAllowed(userInfo?.roles, [Roles.GlobalAdministrator, Roles.TechDeveloper, Roles.Supervisor]) &&
              rowTicket?.stato?.id !== StatoTicket.COMPLETATO &&
              rowTicket?.stato?.id !== StatoTicket.ELIMINATO &&
              rowTicket.attesa &&
              rowTicket?.assegnato &&
              rowTicket?.assegnato.email === userInfo?.email,
            onItemClick: async () => {
              updateQueryData(rowTicket, {
                attesa: false,
              })
              setClearWaitMutation.mutate({ ticket: rowTicket })
            },
          },
          {
            text: 'Invia notifica assistenza scaduta',
            icon: 'bell',
            visible: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles),
            onItemClick: async () => {
              const conditions = [
                !rowTicket?.leg_email_contatto
                  ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : true,
                !rowTicket?.sede
                  ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero inviare la notifica di assistenza scaduta per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      `Invio Notifica Assistenza Scaduta Ticket`,
                    ),
              ]
              if (conditions.every((condition) => condition === true))
                sendSupportExpiredNoticeMutation.mutate({ ticket: rowTicket })
            },
          },
          {
            text: 'Invia notifica assistenza sospesa',
            icon: 'bell',
            visible: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles),
            onItemClick: async () => {
              const conditions = [
                !rowTicket?.leg_email_contatto
                  ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : true,
                !rowTicket?.sede
                  ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero inviare la notifica di assistenza sospesa per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      `Invio Notifica Assistenza Sospesa Ticket`,
                    ),
              ]
              if (conditions.every((condition) => condition === true))
                sendSupportSuspendedNoticeMutation.mutate({ ticket: rowTicket })
            },
          },
          {
            text: 'Invia notifica altro fornitore',
            icon: 'bell',
            visible: isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles),
            onItemClick: async () => {
              const conditions = [
                !rowTicket?.leg_email_contatto
                  ? notify("Email riferimento assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : true,
                !rowTicket?.sede
                  ? notify("Sede assente: impossibile eseguire l' azione.", 'warning', 3000)
                  : await confirm(
                      `<i>Vuoi davvero inviare la notifica di altro fornitore per il ticket <b>${rowTicket.ded_Dis}</b>?</i>`,
                      `Invio Notifica Altro Fornitore Ticket`,
                    ),
              ]
              if (conditions.every((condition) => condition === true))
                sendOtherVendorNoticeMutation.mutate({ ticket: rowTicket })
            },
          },
        )
      }
    },
    [
      assignToMutation,
      cancelMutation,
      clientId,
      defaultCRUDAllowedRoles,
      impiegatiData,
      incrementCallNumberMutation,
      navigate,
      queryClient,
      releaseMutation,
      removeAssigneeMutation,
      sendClosureNoticeMutation,
      sendContactNoticeMutation,
      sendOtherVendorNoticeMutation,
      sendSupportExpiredNoticeMutation,
      sendSupportSuspendedNoticeMutation,
      setActivityRequestMutation,
      setClearWaitMutation,
      setUrgentMutation,
      setWaitMutation,
      takeChargeMutation,
      tipologieTicketData,
      updateQueryData,
      userInfo?.email,
      userInfo?.roles,
    ],
  )
  const infoMaintenanceCellValue = useCallback(
    (rowData: ticket) =>
      rowData.sede?.assistenze
        ?.map((ass) => (ass.sospesa && ass.maintenance?.nome === 'ATTIVA' ? 'SOSPESA' : ass.maintenance?.nome))
        .filter(Boolean)
        .join(' ')
        .trim() || '',
    [],
  )

  const signalRConnectionStatus = useCallback(() => {
    const isConnected = connection?.state === 'Connected' // Assuming 'Connected' is the state string
    const iconClass = isConnected ? 'dx-icon-check' : 'dx-icon-close'
    const iconColor = isConnected ? 'green' : 'red'

    return (
      <i
        className={`dx-icon ${iconClass}`}
        title="Connection Status"
        style={{
          color: iconColor,
          cursor: 'default',
          margin: '5px 0',
        }}
      />
    )
  }, [connection?.state])

  const ticketsDataSource = new DataSource({
    store: new ArrayStore({
      data: ticketsData ?? [],
      key: 'id',
    }),
    reshapeOnPush: true,
  })

  const searchButtonClickHandler = useCallback(
    (e: ColumnButtonClickEvent) => {
      navigate(`${e.row?.data.id}`)
    },
    [navigate],
  )

  const dedDisRender = useCallback(
    (cellData: DataGridTypes.ColumnCellTemplateData) => {
      const data: ticket = cellData.row.data
      return (
        <div className="customLink">
          <ObjectCodeLink
            url={`${data.id}`}
            text={data.ded_Dis}
            disabled={!isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
          ></ObjectCodeLink>
        </div>
      )
    },
    [userInfo?.roles],
  )

  const legPhoneContattoRender = useCallback((cellData: DataGridTypes.ColumnCellTemplateData) => {
    if (cellData.value) return <PhoneNumber phoneNumber={cellData.value} />
  }, [])

  const aziendaRender = useCallback(
    (cellData: DataGridTypes.ColumnCellTemplateData) => {
      const data: ticket = cellData.row.data
      return (
        <ObjectCodeLink
          url={`/clients/${data.sede?.azienda?.id}`}
          text={data.sede?.azienda?.nome}
          disabled={clientId ? true : !isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
        ></ObjectCodeLink>
      )
    },
    [clientId, userInfo?.roles],
  )

  const infoTelefonicaCellValue = useCallback((rowData: ticket) => {
    return (
      rowData.sede?.assistenze
        ?.map((ass: assistenza) =>
          ass.sospesa && (ass.telefonica?.nome === 'STANDARD' || ass.telefonica?.nome === 'PRIME')
            ? 'SOSPESA'
            : ass.telefonica?.nome,
        )
        .filter(Boolean)
        .join(' ')
        .trim() || ''
    )
  }, [])

  if (isLoading) {
    return <GlobalLoadingComponent />
  }
  return (
    <>
      {!clientId && <h2 className={'content-block'}>{title}</h2>}
      <div className={`${clientId ? '' : 'content-block'} grid-container`}>
        <LoadingPanel visible={loadPanelVisible} shading={false} />
        <DataGrid<ticket>
          id={clientId ? 'client-tickets-datagrid-v2' : 'tickets-datagrid-v2'}
          height={getGridHeight()}
          dataSource={ticketsDataSource}
          noDataText="Nessun ticket trovato"
          className={'dx-card wide-card'}
          showBorders={false}
          showColumnLines={true}
          renderAsync={false}
          wordWrapEnabled={false}
          showRowLines={true}
          focusedRowEnabled={true}
          columnAutoWidth={false}
          rowAlternationEnabled={true}
          loadPanel={loadingPanelConfig}
          allowColumnResizing={true}
          allowColumnReordering={true}
          width="100%"
          onExporting={exportingHandler}
          ref={gridRef}
          filterSyncEnabled={true}
          onCellPrepared={cellPrepared}
          groupPanel={groupPanelConfig}
          onRowDblClick={rowDblClickHandler}
          onEditorPreparing={editorPreparingHandler}
          onContextMenuPreparing={contextMenuPreparingHandler}
          onRowExpanding={rowExpandingHandler}
          onRowClick={rowClickHandler}
          columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
        >
          <Pager
            visible={true}
            allowedPageSizes={'auto'}
            displayMode={'compact'}
            showPageSizeSelector={false}
            showInfo={true}
            showNavigationButtons={false}
            infoText={gridTotalItemsLabel}
          />
          <FilterRow visible={true} />
          <Sorting mode="multiple"></Sorting>
          <SearchPanel visible={true} width={currentScreenSize.isXSmall ? 180 : 250} />
          <Export enabled={true} />
          <FilterPanel visible={true} />
          <Editing allowAdding={true} />
          <HeaderFilter visible={true} />
          <Selection mode="single" />
          <ColumnChooser enabled={true} />
          <MasterDetail enabled={true} component={TicketBriefDetail} />
          <Scrolling mode="infinite" />
          <StateStoring
            enabled={true}
            type="localStorage"
            storageKey={clientId ? 'client-tickets-datagrid-v2' : 'tickets-datagrid-v2'}
            savingTimeout={50}
          />
          <Column visible={false} type="buttons" alignment="left" showInColumnChooser={false}>
            <GridButton hint="Details" icon="search" onClick={searchButtonClickHandler}></GridButton>
          </Column>
          <Column
            dataField={'id'}
            caption="ID"
            defaultSortOrder="desc"
            allowSearch={false}
            visible={false}
            dataType="number"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 1 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'id'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'contattato'}
            caption="C"
            allowHeaderFiltering={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 5 : undefined}
          ></Column>
          <Column
            dataField={'attesa'}
            caption="W"
            allowHeaderFiltering={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 6 : undefined}
          />
          <Column
            dataField={'urgente'}
            caption={'U'}
            cellRender={(cellData) => (cellData.value ? <i className="dx-icon-errorcircle"></i> : <></>)}
            allowHeaderFiltering={false}
            visible={currentScreenSize.isLarge || currentScreenSize.isMedium}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 7 : undefined}
          />
          <Column
            dataField={'notifica_chiusura'}
            caption="N"
            cellRender={(cellData) =>
              cellData.value && cellData.value === true ? <i className="dx-icon-bell"></i> : <></>
            }
            allowHeaderFiltering={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 8 : undefined}
          />
          <Column
            dataField={'rating'}
            dataType="number"
            caption={'RATING'}
            cellRender={(cellData) =>
              cellData.value && cellData.value > 0 ? <Rating value={cellData.value} /> : <></>
            }
            visible={clientId ? true : false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 9 : undefined}
          >
            <HeaderFilter
              dataSource={[
                {
                  text: '0',
                  value: 0,
                },
                {
                  text: '1',
                  value: 1,
                },
                {
                  text: '2',
                  value: 2,
                },
                {
                  text: '3',
                  value: 3,
                },
                {
                  text: '4',
                  value: 4,
                },
                {
                  text: '5',
                  value: 5,
                },
              ]}
            ></HeaderFilter>
          </Column>

          <Column
            dataField={'ded_Dis'}
            cellRender={dedDisRender}
            caption={'TICKET ID'}
            allowSearch={false}
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 80 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 80 : undefined}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 30 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'ded_Dis'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField={'creatore.fullname'}
            caption={'CREATORE'}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 10 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'fullname'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField={'sede.azienda.nome'}
            cellRender={aziendaRender}
            caption={'AZIENDA'}
            visible={clientId === undefined}
            width={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 120 : undefined}
            minWidth={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 120 : undefined}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 29 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'sede.nome'}
            caption={'SEDE'}
            allowHeaderFiltering={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 23 : undefined}
          />
          <Column
            dataField={'sede.azienda.agente.commerciale_qs.fullname'}
            caption={'AGENTE'}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 12 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'commerciale_qs.fullname'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'sede.azienda.rivenditore.nome'}
            caption={'RIVENDITORE'}
            visible={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 11 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'rivenditore.nome'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField={'leg_contatto'}
            caption={'CONTATTO'}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 27 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'leg_contatto'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'leg_email_contatto'}
            caption="EMAIL"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 26 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'leg_email_contatto'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'leg_phone_contatto'}
            cellRender={legPhoneContattoRender}
            caption="TELEFONO"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 25 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'leg_phone_contatto'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField="apertura"
            caption="APERTURA"
            dataType="date"
            format="dd/MM/yyyy HH:mm"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 24 : undefined}
          />
          <Column
            dataField="tipologia_ticket.nome"
            caption="CATEGORIA"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 22 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField="stato.nome"
            caption="STATO"
            allowHeaderFiltering={true}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 14 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField="assegnato.fullname"
            caption="ASSEGNATO"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 28 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'fullname'} />
            </HeaderFilter>
          </Column>

          <Column
            dataField="incaricato.fullname"
            caption="IN CARICO DA"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 15 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'fullname'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField="durata"
            caption="DURATA"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 13 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'durata'} />
            </HeaderFilter>
          </Column>
          <Column
            name={'info_telefonica'}
            caption={'TELEFONICA'}
            calculateCellValue={infoTelefonicaCellValue}
            allowHeaderFiltering={true}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 16 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          <Column
            name={'info_maintenance'}
            caption={'MAINTENANCE'}
            calculateCellValue={infoMaintenanceCellValue}
            allowHeaderFiltering={true}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 17 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'nome'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField={'customer_id'}
            caption="CUSTOMER ID"
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 2 : undefined}
          >
            <HeaderFilter>
              <Search enabled={true} searchExpr={'customer_id'} />
            </HeaderFilter>
          </Column>
          <Column
            dataField="descrizione"
            caption="MOTIVO"
            visible={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 3 : undefined}
          />
          <Column
            dataField="soluzione"
            caption="SOLUZIONE"
            visible={false}
            hidingPriority={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 4 : undefined}
          />

          <Toolbar>
            <Item location="before" render={signalRConnectionStatus}></Item>

            <Item
              location="before"
              locateInMenu="never"
              showText="inMenu"
              widget="dxButton"
              options={refreshButtonOptions}
            ></Item>
            <Item name="groupPanel" />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={newTicketButtonOptions}
            />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, defaultCRUDAllowedRoles)}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              name="addRowButton"
              options={duplicateButtonOptions}
            />
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [Roles.Guest])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={historicButtonOptions}
            ></Item>
            <Item
              visible={isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.TechDeveloper,
                Roles.Administrator,
              ])}
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              widget="dxButton"
              options={supportManulButtonOptions}
            ></Item>
            <Item
              name="columnChooserButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Scelta colonne', text: 'Scelta colonne' }}
            />
            <Item
              name="exportButton"
              locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
              showText="inMenu"
              options={{ hint: 'Esporta dati in excel', text: 'Esporta dati in excel' }}
            />
            <Item name="searchPanel" />
          </Toolbar>
        </DataGrid>
        <Popup
          width={300}
          height={'auto'}
          hideOnOutsideClick={true}
          showCloseButton={true}
          deferRendering={true}
          title={'Assegna a'}
          ref={popupAssignRef}
          className="assignto-popup-content"
          resizeEnabled={false}
        >
          <AssignSelector ticket={selectedTicket} popupAssignRef={popupAssignRef} clientId={clientId} />
        </Popup>
      </div>
    </>
  )
}

export default TicketsPage
